import {Phone} from 'lucide-react';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {renderRichText} from '@client/pages/public/enroll/richText';
import {COVERAGE_ICON_POOL} from '@client/pages/public/enroll/coverageIcons';
import {CONTACT_TOKEN, siteDisclosures} from '@common/enrollmentSite/defaults/disclosures';
import {siteHighlight} from '@common/enrollmentSite/defaults/highlight';
import type {HighlightCard, ResourceDisclosure} from '@common/enrollmentSite/types';

/** Highlight card beside the "Required disclosures" box, shared by the resource pages. */
export function HighlightAndDisclosures({
  highlight: stored,
  disclosures: storedDisclosures,
}: {
  highlight: HighlightCard | null | undefined;
  disclosures: ResourceDisclosure[] | undefined;
}) {
  const group = useEnrollGroup();
  const highlight = siteHighlight(stored);
  const disclosures = siteDisclosures(storedDisclosures);
  const HighlightIcon = COVERAGE_ICON_POOL[highlight?.icon ?? 'stethoscope'].Icon;
  const contact = group.benefitsContact ?? 'HR office';
  const phoneDigits = group.supportPhone.replace(/\D/g, '');

  return (
    <div className='grid gap-4 md:grid-cols-2'>
      {highlight && (
        <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
          <div className='flex items-center gap-3'>
            <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-blue-100 text-[var(--enroll-primary)]'>
              <HighlightIcon className='h-5 w-5' />
            </span>
            <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>{highlight.title}</h2>
          </div>
          <p className='mt-3 text-sm leading-relaxed text-neutral-600'>
            {renderRichText(highlight.body)}
          </p>
          {highlight.closing && (
            <p className='mt-3 text-sm leading-relaxed text-neutral-500'>
              {renderRichText(highlight.closing)}
            </p>
          )}
        </section>
      )}

      <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
        {disclosures.length > 0 && (
          <>
            <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>Required disclosures</h2>
            <dl className='mt-4 space-y-3 text-sm leading-relaxed'>
              {disclosures.map((disclosure, i) => (
                <div key={i}>
                  <dt className='inline font-semibold text-neutral-800'>{disclosure.label}</dt>{' '}
                  <dd className='inline text-neutral-600'>
                    — {renderRichText(disclosure.text.replaceAll(CONTACT_TOKEN, contact))}
                  </dd>
                </div>
              ))}
            </dl>
          </>
        )}
        <p
          className={`flex items-center gap-2 text-sm text-neutral-600 ${
            disclosures.length > 0 ? 'mt-4 border-t border-neutral-200 pt-4' : ''
          }`}
        >
          <Phone className='h-4 w-4 flex-none text-[var(--enroll-primary)]' />
          Questions? Call the Concierge Team at{' '}
          <a
            href={`tel:${phoneDigits}`}
            className='font-semibold text-[var(--enroll-primary)] hover:underline'
          >
            {group.supportPhone}
          </a>
        </p>
      </section>
    </div>
  );
}
